// Import statements
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Alert,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  useColorScheme as useDeviceColorScheme,
  TextInput,
  Pressable,
  FlatList,
  Platform,
  Modal,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";
import moment from "moment-timezone";
import { FIREBASE_AUTH } from "../../FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import CustomLoading from "./CustomLoading";
import CustomAlert from "./CustomAlert";
import jsPDF from "jspdf";
import RNHTMLtoPDF from "react-native-html-to-pdf";

const API_BASE_URL = "https://sbg-backend.onrender.com";

const BackButton = () => {
  const navigation = useNavigation();
  return (
    <TouchableOpacity
      style={styles.backButton}
      onPress={() => navigation.goBack()}
    >
      <Feather name="arrow-left" size={24} color="#007AFF" />
    </TouchableOpacity>
  );
};

const StatsSection = ({ colorScheme, stats }) => (
  <View style={styles.statsContainer}>
    <View
      style={[styles.statsCard, colorScheme === "dark" && styles.statsCardDark]}
    >
      <MaterialCommunityIcons
        name="calendar-check"
        size={24}
        color={colorScheme === "dark" ? "#34C759" : "#2ECC71"}
      />
      <Text
        style={[
          styles.statsValue,
          colorScheme === "dark" && styles.statsValueDark,
        ]}
      >
        {stats.approved}
      </Text>
      <Text
        style={[
          styles.statsLabel,
          colorScheme === "dark" && styles.statsLabelDark,
        ]}
      >
        Approved
      </Text>
    </View>

    <View
      style={[styles.statsCard, colorScheme === "dark" && styles.statsCardDark]}
    >
      <MaterialCommunityIcons
        name="calendar-clock"
        size={24}
        color={colorScheme === "dark" ? "#007AFF" : "#3498DB"}
      />
      <Text
        style={[
          styles.statsValue,
          colorScheme === "dark" && styles.statsValueDark,
        ]}
      >
        {stats.pending}
      </Text>
      <Text
        style={[
          styles.statsLabel,
          colorScheme === "dark" && styles.statsLabelDark,
        ]}
      >
        Pending
      </Text>
    </View>

    <View
      style={[styles.statsCard, colorScheme === "dark" && styles.statsCardDark]}
    >
      <MaterialCommunityIcons
        name="calendar-remove"
        size={24}
        color={colorScheme === "dark" ? "#FF3B30" : "#FF3B30"}
      />
      <Text
        style={[
          styles.statsValue,
          colorScheme === "dark" && styles.statsValueDark,
        ]}
      >
        {stats.rejected}
      </Text>
      <Text
        style={[
          styles.statsLabel,
          colorScheme === "dark" && styles.statsLabelDark,
        ]}
      >
        Rejected
      </Text>
    </View>
  </View>
);

const sortLeaveRequestsByRequestedDate = (a, b) => {
  const dateA = moment(a.dateFrom, "YYYY-MM-DD").valueOf();
  const dateB = moment(b.dateFrom, "YYYY-MM-DD").valueOf();

  if (dateA !== dateB) {
    return dateA - dateB;
  }

  return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
};

const LeaveReport = () => {
  // State variables
  const navigation = useNavigation();
  const deviceColorScheme = useDeviceColorScheme();
  const [colorScheme, setColorScheme] = useState(deviceColorScheme);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [allLeaveRequests, setAllLeaveRequests] = useState([]);
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState({
    approved: 0,
    pending: 0,
    rejected: 0,
  });
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [monthPickerValue, setMonthPickerValue] = useState(
    moment().startOf("month")
  );
  const [deleteAlertVisible, setDeleteAlertVisible] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const monthLabels = useMemo(() => moment.months(), []);

  // Functions
  const fetchUserData = useCallback(async () => {
    const user = FIREBASE_AUTH.currentUser;
    if (user) {
      try {
        const db = getFirestore();
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUserData({ ...docSnap.data(), uid: user.uid });
        } else {
          console.log("No user data found");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data");
      }
    }
  }, []);

  const fetchLeaveRequests = useCallback(async () => {
    if (!userData) return;

    setLoading(true);
    setError(null);
    try {
      const startDate = currentMonth
        .clone()
        .startOf("month")
        .format("YYYY-MM-DD");
      const endDate = currentMonth.clone().endOf("month").format("YYYY-MM-DD");

      let allEmployeesResponse;
      if (userData.role === "admin") {
        allEmployeesResponse = await axios.get(`${API_BASE_URL}/employees`);
      } else if (userData.role === "supervisor") {
        allEmployeesResponse = await axios.get(`${API_BASE_URL}/employees`, {
          params: {
            supervisorName: userData.name,
            supervisorEmail:
              userData.email || FIREBASE_AUTH.currentUser?.email || "",
          },
        });
      } else {
        allEmployeesResponse = {
          data: [
            {
              employeeId: userData.employeeId,
              employeeName: userData.name,
            },
          ],
        };
      }

      const leaveRequests = await Promise.all(
        allEmployeesResponse.data.map(async (employee) => {
          const leaveResponse = await axios.get(
            `${API_BASE_URL}/personal-leave/${employee.employeeId}`,
            { params: { startDate, endDate } }
          );
          return leaveResponse.data.map((request) => ({
            ...request,
            employeeName: employee.employeeName,
            employeeId: employee.employeeId,
            // Use the date string directly from the backend to avoid timezone conversion issues
            dateFrom: request.dateFrom,
            dateTo: request.dateTo,
            approvedDate: request.approvedDate || null,
          }));
        })
      );

      // Sort requests by requested leave date so admins see the earliest time off first
      const flattenedLeaveRequests = leaveRequests
        .flat()
        .sort(sortLeaveRequestsByRequestedDate);
      setLeaveRequests(flattenedLeaveRequests);
      setFilteredLeaveRequests(flattenedLeaveRequests);
    } catch (err) {
      console.error("Error fetching leave requests:", err);
      setError(
        err.message || "Failed to fetch leave requests. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [userData, currentMonth]);

  const fetchAllLeaveRequests = useCallback(async () => {
    if (!userData) return;

    try {
      let allEmployeesResponse;
      if (userData.role === "admin") {
        allEmployeesResponse = await axios.get(`${API_BASE_URL}/employees`);
      } else if (userData.role === "supervisor") {
        allEmployeesResponse = await axios.get(`${API_BASE_URL}/employees`, {
          params: {
            supervisorName: userData.name,
            supervisorEmail:
              userData.email || FIREBASE_AUTH.currentUser?.email || "",
          },
        });
      } else {
        allEmployeesResponse = {
          data: [
            {
              employeeId: userData.employeeId,
              employeeName: userData.name,
            },
          ],
        };
      }

      const leaveRequests = await Promise.all(
        allEmployeesResponse.data.map(async (employee) => {
          const leaveResponse = await axios.get(
            `${API_BASE_URL}/personal-leave/${employee.employeeId}`
          );
          return leaveResponse.data.map((request) => ({
            ...request,
            employeeName: employee.employeeName,
            employeeId: employee.employeeId,
            dateFrom: request.dateFrom,
            dateTo: request.dateTo,
            approvedDate: request.approvedDate || null,
          }));
        })
      );

      const flattenedLeaveRequests = leaveRequests
        .flat()
        .sort(sortLeaveRequestsByRequestedDate);
      setAllLeaveRequests(flattenedLeaveRequests);
    } catch (err) {
      console.error("Error fetching all leave requests:", err);
    }
  }, [userData]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (userData) {
      fetchLeaveRequests();
    }
  }, [fetchLeaveRequests, userData, currentMonth]);

  useEffect(() => {
    if (userData) {
      fetchAllLeaveRequests();
    }
  }, [fetchAllLeaveRequests, userData]);

  useEffect(() => {
    const sourceRequests = searchInput.trim()
      ? allLeaveRequests
      : leaveRequests;

    const filtered = sourceRequests
      .filter((request) =>
        request.employeeName.toLowerCase().includes(searchInput.toLowerCase())
      )
      .sort(sortLeaveRequestsByRequestedDate);
    setFilteredLeaveRequests(filtered);
    setCurrentPage(1);
  }, [searchInput, leaveRequests, allLeaveRequests]);

  useEffect(() => {
    const approved = leaveRequests.filter(
      (req) => req.status === "Approved"
    ).length;
    const pending = leaveRequests.filter(
      (req) => req.status === "Pending"
    ).length;
    const rejected = leaveRequests.filter(
      (req) => req.status === "Rejected"
    ).length;

    setStats({
      approved,
      pending,
      rejected,
    });
  }, [leaveRequests]);

  // ----- Management action handlers -----
  const updateRequestStatus = useCallback(async (requestId, newStatus) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === "Approved" && userData?.name) {
        payload.approvedBy = userData.name;
      }
      await axios.put(`${API_BASE_URL}/personal-leave/${requestId}`, payload);
      // Optimistically update UI
      setLeaveRequests((prev) =>
        prev.map((r) =>
          r._id === requestId ? { ...r, status: newStatus, approvedBy: payload.approvedBy, approvedDate: new Date() } : r
        )
      );
      setAllLeaveRequests((prev) =>
        prev.map((r) =>
          r._id === requestId ? { ...r, status: newStatus, approvedBy: payload.approvedBy, approvedDate: new Date() } : r
        )
      );
    } catch (err) {
      console.error("Error updating status", err);
      Alert.alert("Error", "Failed to update request status. Please try again.");
    }
  }, [userData]);

  const deleteRequest = useCallback((requestId) => {
    setDeleteRequestId(requestId);
    setDeleteAlertVisible(true);
  }, []);

  const confirmDeleteRequest = useCallback(async () => {
    if (!deleteRequestId) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/personal-leave/${deleteRequestId}`);
      setLeaveRequests((prev) => prev.filter((r) => r._id !== deleteRequestId));
      setAllLeaveRequests((prev) => prev.filter((r) => r._id !== deleteRequestId));
    } catch (err) {
      console.error("Error deleting request", err);
      Alert.alert("Error", "Failed to delete request. Please try again.");
    } finally {
      setDeleteAlertVisible(false);
      setDeleteRequestId(null);
    }
  }, [deleteRequestId]);

  const toggleColorScheme = () => {
    setColorScheme((prevScheme) => (prevScheme === "light" ? "dark" : "light"));
  };

  const openMonthPicker = () => {
    setMonthPickerValue(currentMonth.clone().startOf("month"));
    setShowMonthPicker(true);
  };

  const setPickerMonth = (monthIndex) => {
    setMonthPickerValue((prev) => prev.clone().month(monthIndex));
  };

  const shiftPickerYear = (direction) => {
    setMonthPickerValue((prev) => prev.clone().add(direction, "year"));
  };

  const applyMonthPicker = () => {
    setCurrentPage(1);
    setCurrentMonth(monthPickerValue.clone().startOf("month"));
    setShowMonthPicker(false);
  };

  const cancelMonthPicker = () => {
    setShowMonthPicker(false);
  };

  const dynamicStyles = useMemo(
    () => createDynamicStyles(colorScheme),
    [colorScheme]
  );

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredLeaveRequests.slice(startIndex, endIndex);
  }, [filteredLeaveRequests, currentPage, itemsPerPage]);

  const PaginationControls = () => {
    const totalPages = Math.ceil(filteredLeaveRequests.length / itemsPerPage);

    if (totalPages <= 1) return null;

    return (
      <View
        style={[
          styles.paginationContainer,
          colorScheme === "dark" && styles.paginationContainerDark,
        ]}
      >
        <TouchableOpacity
          onPress={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={[
            styles.paginationButton,
            currentPage === 1 && styles.paginationButtonDisabled,
          ]}
        >
          <Feather
            name="chevron-left"
            size={24}
            color={currentPage === 1 ? "#8E8E93" : "#007AFF"}
          />
        </TouchableOpacity>

        <Text
          style={[
            styles.paginationText,
            colorScheme === "dark" && styles.paginationTextDark,
          ]}
        >
          Page {currentPage} of {totalPages}
        </Text>

        <TouchableOpacity
          onPress={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          style={[
            styles.paginationButton,
            currentPage === totalPages && styles.paginationButtonDisabled,
          ]}
        >
          <Feather
            name="chevron-right"
            size={24}
            color={currentPage === totalPages ? "#8E8E93" : "#007AFF"}
          />
        </TouchableOpacity>
      </View>
    );
  };
  const renderLeaveRequest = ({ item: request }) => {
    const isPending = request.status === "Pending";
  return (
    <View
      style={[
        styles.requestItem,
        colorScheme === "dark" && styles.requestItemDark,
        {
          backgroundColor: colorScheme === "dark" ? "#1E1E1E" : "white",
        },
      ]}
    >
      <Pressable
        onPress={
          isPending
            ? () =>
                navigation.navigate("Leave Request", {
                  employeeId: request.employeeId,
                  requestId: request._id,
                })
            : undefined
        }
        style={({ pressed }) => [
          styles.requestInfo,
          {
            backgroundColor: pressed
              ? colorScheme === "dark"
                ? "#333"
                : "#F0F0F0"
              : "transparent",
          },
        ]}
      >
        <Text
          style={[
            styles.employeeName,
            colorScheme === "dark" && styles.employeeNameDark,
          ]}
        >
          {request.employeeName}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          {moment(request.dateFrom).format("MMM D")} -{" "}
          {moment(request.dateTo).format("MMM D, YYYY")}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          Client: {request.clientName}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          Leave Type: {request.leaveType}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          Hours Claimed: {request.hoursClaimed}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          Total Days Off: {request.totalDaysOff}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          Reason: {request.reasonForAbsence}
        </Text>
        <Text
          style={[
            styles.requestDetail,
            colorScheme === "dark" && styles.requestDetailDark,
          ]}
        >
          Status: {request.status}
        </Text>
        {request.approvedBy && (
          <Text
            style={[
              styles.requestDetail,
              colorScheme === "dark" && styles.requestDetailDark,
            ]}
          >
            Approved By: {request.approvedBy}
          </Text>
        )}
        {request.approvedDate && (
          <Text
            style={[
              styles.requestDetail,
              colorScheme === "dark" && styles.requestDetailDark,
            ]}
          >
            Approved Date: {moment(request.approvedDate).format("MMM D, YYYY")}
          </Text>
        )}
      </Pressable>

      {userData && (userData.role === "admin" || userData.role === "supervisor") && (
        <View style={styles.actionButtonContainer}>
          {request.status !== "Approved" && (
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: "#34C759" }]}
              onPress={() => updateRequestStatus(request._id, "Approved")}
            >
              <Text style={styles.actionButtonText}>Approve</Text>
            </TouchableOpacity>
          )}
          {request.status !== "Rejected" && (
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: "#FF3B30" }]}
              onPress={() => updateRequestStatus(request._id, "Rejected")}
            >
              <Text style={styles.actionButtonText}>Reject</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: "#8E8E93" }]}
            onPress={() => deleteRequest(request._id)}
          >
            <Text style={styles.actionButtonText}>Delete</Text>
          </TouchableOpacity>
        </View>
      )}

      <TouchableOpacity
        style={styles.pdfButton}
        onPress={() => generatePDF(request)}
      >
        <Feather name="file-text" size={24} color="#007AFF" />
      </TouchableOpacity>
      {isPending && <Feather name="chevron-right" size={24} color="#C7C7CC" />}
    </View>
  );
  };

  const generatePDF = async (request) => {
    if (Platform.OS === "web") {
      // Web PDF generation using jsPDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = 20;

      // Helper function to add wrapped text and return new Y position
      const addWrappedText = (label, value) => {
        doc.setFontSize(12);
        doc.text(`${label}:`, margin, yPosition);

        // Calculate available width for the value
        const labelWidth = doc.getTextWidth(`${label}: `);
        const maxWidth = pageWidth - margin * 2 - labelWidth;

        // Split long text into lines
        const lines = doc.splitTextToSize(value, maxWidth);

        // Print each line
        lines.forEach((line, index) => {
          if (index === 0) {
            // First line goes next to label
            doc.text(line, margin + labelWidth, yPosition);
          } else {
            // Subsequent lines are indented
            yPosition += 7;
            doc.text(line, margin + labelWidth, yPosition);
          }
        });

        yPosition += 12; // Add space after the field
        return yPosition;
      };

      // Header
      doc.setFontSize(18);
      doc.text("Leave Request Details", pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 20;

      // Employee details
      addWrappedText("Employee Name", request.employeeName);
      addWrappedText("Client Name", request.clientName);
      addWrappedText("Leave Type", request.leaveType);
      addWrappedText("Hours Claimed", request.hoursClaimed.toString());
      // Fix date handling to ensure consistent display across all views
      const fromDate = moment(request.dateFrom).format("MMM D, YYYY");
      const toDate = moment(request.dateTo).format("MMM D, YYYY");
      addWrappedText("Date Range", `${fromDate} - ${toDate}`);
      addWrappedText("Total Days Off", request.totalDaysOff.toString());
      addWrappedText("Reason for Absence", request.reasonForAbsence);
      addWrappedText("Status", request.status);

      if (request.approvedBy) {
        addWrappedText("Approved By", request.approvedBy);
      }
      if (request.approvedDate) {
        addWrappedText("Approved Date", moment(request.approvedDate).format("MMM D, YYYY"));
      }

      // Company details
      doc.setFontSize(10);
      doc.text("Scott Business Group", margin, yPosition);
      yPosition += 5;
      doc.text(
        "4600 East Washington Street, Suite 300, Phoenix, Arizona 85034",
        margin,
        yPosition
      );
      yPosition += 5;
      doc.text(
        "T: 602.604.9928 | F: 623.321.1327 | E: timesheets@scottbiz.net",
        margin,
        yPosition
      );
      yPosition += 15;


      // Add company policy text
      yPosition += 10;
      doc.setFontSize(10);
      const policyText = `Under the ARIZONA FAIR WAGES AND HEALTHY FAMILIES ACT, Earned Paid Sick Time may be used for medical care, public health emergencies, or absence due to domestic violence. Documentation is required for three or more consecutive days of absence.`;

      const splitText = doc.splitTextToSize(policyText, pageWidth - margin * 2);
      doc.text(splitText, margin, yPosition);

      // Save the PDF
      const fileName = `Leave_Request_${request.employeeId}_${moment(
        request.dateFrom
      ).format("YYYYMMDD")}.pdf`;
      doc.save(fileName);
    } else {
      // React Native PDF generation
      try {
        const htmlContent = `
          <html>
            <head>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  padding: 20px; 
                  line-height: 1.6;
                }
                h1 { 
                  text-align: center; 
                  color: #333; 
                  margin-bottom: 20px;
                }
                .company-info {
                  text-align: left;
                  font-size: 0.9em;
                  margin-bottom: 20px;
                }
                .field { 
                  margin: 10px 0;
                  word-wrap: break-word;
                }
                .label { 
                  font-weight: bold;
                  display: inline-block;
                  min-width: 120px;
                }
                .value {
                  display: inline-block;
                  max-width: calc(100% - 130px);
                  vertical-align: top;
                }
                .policy { 
                  margin-top: 30px;
                  font-size: 0.9em;
                  color: #666;
                  padding: 15px;
                  background-color: #f9f9f9;
                  border-radius: 5px;
                }
              </style>
            </head>
            <body>
              <h1>Leave Request Details</h1>
              <div class="company-info">
                Scott Business Group<br>
                4600 East Washington Street, Suite 300, Phoenix, Arizona 85034<br>
                T: 602.604.9928 | F: 623.321.1327 | E: timesheets@scottbiz.net
              </div>
              <div class="field"><span class="label">Employee Name:</span> <span class="value">${
                request.employeeName
              }</span></div>
              <div class="field"><span class="label">Employee ID:</span> <span class="value">${
                request.employeeId
              }</span></div>
              <div class="field"><span class="label">Client:</span> <span class="value">${
                request.clientName
              }</span></div>
              <div class="field"><span class="label">Leave Type:</span> <span class="value">${
                request.leaveType
              }</span></div>
              <div class="field"><span class="label">Hours Claimed:</span> <span class="value">${
                request.hoursClaimed
              }</span></div>
              <div class="field"><span class="label">Date From:</span> <span class="value">${moment(
                request.dateFrom
              ).format("MMM D, YYYY")}</span></div>
              <div class="field"><span class="label">Date To:</span> <span class="value">${moment(
                request.dateTo
              ).format("MMM D, YYYY")}</span></div>
              <div class="field"><span class="label">Total Days Off:</span> <span class="value">${
                request.totalDaysOff
              }</span></div>
              <div class="field"><span class="label">Reason:</span> <span class="value">${
                request.reasonForAbsence
              }</span></div>
              <div class="field"><span class="label">Status:</span> <span class="value">${
                request.status
              }</span></div>
              ${
                request.approvedBy
                  ? `<div class="field"><span class="label">Approved By:</span> <span class="value">${request.approvedBy}</span></div>`
                  : ""
              }
              ${
                request.approvedDate
                  ? `<div class="field"><span class="label">Approved Date:</span> <span class="value">${moment(
                      request.approvedDate
                    ).format("MMM D, YYYY")}</span></div>`
                  : ""
              }
              <div class="policy">
                Under the ARIZONA FAIR WAGES AND HEALTHY FAMILIES ACT, Earned Paid Sick Time may be used for medical care, 
                public health emergencies, or absence due to domestic violence. Documentation is required for three or more 
                consecutive days of absence.
              </div>
            </body>
          </html>
        `;

        const options = {
          html: htmlContent,
          fileName: `Leave_Request_${request.employeeId}_${moment(
            request.dateFrom
          ).tz("America/Phoenix").format("YYYYMMDD")}`,
          directory: "Documents",
        };

        const file = await RNHTMLtoPDF.convert(options);
        console.log("PDF generated:", file.filePath);
      } catch (error) {
        console.error("Error generating PDF:", error);
      }
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, dynamicStyles.container]}>
        <View style={dynamicStyles.loadingContainer}>
          <CustomLoading
            isDarkMode={colorScheme === "dark"}
            width={300}
            height={75}
          />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, dynamicStyles.container]}>
      <StatusBar
        barStyle={colorScheme === "dark" ? "light-content" : "dark-content"}
        backgroundColor={colorScheme === "dark" ? "#000" : "#F0F0F0"}
      />
      <View style={styles.content}>
        {/* Header */}
        <View
          style={[styles.header, colorScheme === "dark" && styles.headerDark]}
        >
          <BackButton />
          <Text
            style={[styles.title, colorScheme === "dark" && styles.titleDark]}
          >
            Leave Report
          </Text>
          <TouchableOpacity
            onPress={toggleColorScheme}
            style={styles.themeToggle}
          >
            <Feather
              name={colorScheme === "dark" ? "sun" : "moon"}
              size={24}
              color="#007AFF"
            />
          </TouchableOpacity>
        </View>

        <View style={styles.monthNavContainer}>
          <TouchableOpacity
            style={[
              styles.monthNavButton,
              colorScheme === "dark" && styles.monthNavButtonDark,
            ]}
            onPress={() =>
              setCurrentMonth((prev) => {
                setCurrentPage(1);
                return prev.clone().subtract(1, "month");
              })
            }
            >
              <Feather name="chevron-left" size={18} color="#007AFF" />
              <Text
                style={[
                  styles.monthNavButtonText,
                colorScheme === "dark" && styles.monthNavButtonTextDark,
              ]}
            >
              Prev Month
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.monthNavLabelButton,
              colorScheme === "dark" && styles.monthNavLabelButtonDark,
            ]}
            onPress={openMonthPicker}
          >
            <Feather
              name="calendar"
              size={16}
              color={colorScheme === "dark" ? "#FFFFFF" : "#111111"}
              style={styles.monthNavLabelIcon}
            />
            <Text
              style={[
                styles.monthNavLabel,
                colorScheme === "dark" && styles.monthNavLabelDark,
              ]}
            >
              {currentMonth.format("MMMM YYYY")}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.monthNavButton,
              colorScheme === "dark" && styles.monthNavButtonDark,
            ]}
            onPress={() =>
              setCurrentMonth((prev) => {
                setCurrentPage(1);
                return prev.clone().add(1, "month");
              })
            }
          >
            <Text
              style={[
                styles.monthNavButtonText,
                colorScheme === "dark" && styles.monthNavButtonTextDark,
              ]}
            >
              Next Month
            </Text>
            <Feather name="chevron-right" size={18} color="#007AFF" />
          </TouchableOpacity>
        </View>

        <Text
          style={[
            styles.monthHint,
            colorScheme === "dark" && styles.monthHintDark,
          ]}
        >
          Tap the month to pick a date period
        </Text>

        <Modal
          visible={showMonthPicker}
          transparent
          animationType="fade"
          onRequestClose={cancelMonthPicker}
        >
          <View style={styles.pickerOverlay}>
            <View
              style={[
                styles.pickerCard,
                colorScheme === "dark" && styles.pickerCardDark,
              ]}
            >
              <Text
                style={[
                  styles.pickerTitle,
                  colorScheme === "dark" && styles.pickerTitleDark,
                ]}
              >
                Select Month
              </Text>
              <View style={styles.pickerYearRow}>
                <TouchableOpacity
                  style={styles.pickerYearButton}
                  onPress={() => shiftPickerYear(-1)}
                >
                  <Feather name="chevron-left" size={18} color="#007AFF" />
                </TouchableOpacity>
                <Text
                  style={[
                    styles.pickerYearText,
                    colorScheme === "dark" && styles.pickerYearTextDark,
                  ]}
                >
                  {monthPickerValue.format("YYYY")}
                </Text>
                <TouchableOpacity
                  style={styles.pickerYearButton}
                  onPress={() => shiftPickerYear(1)}
                >
                  <Feather name="chevron-right" size={18} color="#007AFF" />
                </TouchableOpacity>
              </View>
              <View style={styles.monthGrid}>
                {monthLabels.map((label, monthIndex) => {
                  const isSelected = monthPickerValue.month() === monthIndex;
                  return (
                    <TouchableOpacity
                      key={label}
                      style={[
                        styles.monthGridItem,
                        isSelected && styles.monthGridItemActive,
                        colorScheme === "dark" &&
                          styles.monthGridItemDark,
                        isSelected &&
                          colorScheme === "dark" &&
                          styles.monthGridItemActiveDark,
                      ]}
                      onPress={() => setPickerMonth(monthIndex)}
                    >
                      <Text
                        style={[
                          styles.monthGridItemText,
                          isSelected && styles.monthGridItemTextActive,
                          colorScheme === "dark" &&
                            styles.monthGridItemTextDark,
                          isSelected &&
                            colorScheme === "dark" &&
                            styles.monthGridItemTextActiveDark,
                        ]}
                      >
                        {label.slice(0, 3)}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              <View style={styles.pickerActions}>
                <TouchableOpacity
                  style={[styles.pickerButton, styles.pickerCancelButton]}
                  onPress={cancelMonthPicker}
                >
                  <Text style={styles.pickerCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.pickerButton, styles.pickerApplyButton]}
                  onPress={applyMonthPicker}
                >
                  <Text style={styles.pickerApplyText}>Apply</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Stats Section */}
        <StatsSection colorScheme={colorScheme} stats={stats} />

        {/* Search Input */}
        <View
          style={[
            styles.searchContainer,
            colorScheme === "dark" && styles.searchContainerDark,
          ]}
        >
          <Feather
            name="search"
            size={20}
            color={colorScheme === "dark" ? "#8E8E93" : "#8E8E93"}
            style={styles.searchIcon}
          />
          <TextInput
            style={[
              styles.searchInput,
              colorScheme === "dark" && styles.searchInputDark,
            ]}
            placeholder="Search by name"
            placeholderTextColor={
              colorScheme === "dark" ? "#8E8E93" : "#8E8E93"
            }
            value={searchInput}
            onChangeText={setSearchInput}
          />
        </View>

        {/* Leave Requests */}
        <FlatList
          data={paginatedData}
          renderItem={renderLeaveRequest}
          keyExtractor={(item) => item._id}
          ListEmptyComponent={
            <Text
              style={[
                styles.noRequestsText,
                colorScheme === "dark" && styles.noRequestsTextDark,
              ]}
            >
              No leave requests found for this period.
            </Text>
          }
          contentContainerStyle={styles.requestList}
          ListFooterComponent={<PaginationControls />}
        />
      </View>

      <CustomAlert
        visible={deleteAlertVisible}
        title="Confirm Delete"
        message="Are you sure you want to delete this request?"
        onClose={() => {
          setDeleteAlertVisible(false);
          setDeleteRequestId(null);
        }}
        onConfirm={confirmDeleteRequest}
        showCancel={true}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // Container styles
  container: {
    flex: 1,
  },
  // Header styles
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  headerDark: {
    backgroundColor: "#1E1E1E",
    borderBottomColor: "#333333",
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    color: "#000000",
  },
  titleDark: {
    color: "#FFFFFF",
  },
  monthNavContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  monthNavButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#F2F2F7",
  },
  monthNavButtonDark: {
    backgroundColor: "#2C2C2E",
  },
  monthNavButtonText: {
    color: "#007AFF",
    fontWeight: "600",
  },
  monthNavButtonTextDark: {
    color: "#7AB7FF",
  },
  monthNavLabel: {
    flex: 1,
    textAlign: "center",
    fontSize: 16,
    fontWeight: "700",
    color: "#111111",
  },
  monthNavLabelDark: {
    color: "#FFFFFF",
  },
  monthNavLabelButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#F2F2F7",
  },
  monthNavLabelButtonDark: {
    backgroundColor: "#2C2C2E",
  },
  monthNavLabelIcon: {
    marginTop: 1,
  },
  monthHint: {
    marginTop: -2,
    marginBottom: 10,
    paddingHorizontal: 16,
    textAlign: "center",
    fontSize: 12,
    color: "#6B7280",
  },
  monthHintDark: {
    color: "#A1A1AA",
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  pickerCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 16,
  },
  pickerCardDark: {
    backgroundColor: "#1E1E1E",
  },
  pickerTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111111",
    textAlign: "center",
    marginBottom: 12,
  },
  pickerTitleDark: {
    color: "#FFFFFF",
  },
  pickerYearRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  pickerYearButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F2F2F7",
  },
  pickerYearText: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111111",
  },
  pickerYearTextDark: {
    color: "#FFFFFF",
  },
  monthGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 8,
  },
  monthGridItem: {
    width: "30%",
    minWidth: 90,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F2F2F7",
    marginBottom: 8,
  },
  monthGridItemDark: {
    backgroundColor: "#2C2C2E",
  },
  monthGridItemActive: {
    backgroundColor: "#007AFF",
  },
  monthGridItemActiveDark: {
    backgroundColor: "#0A84FF",
  },
  monthGridItemText: {
    fontWeight: "700",
    color: "#111111",
  },
  monthGridItemTextDark: {
    color: "#FFFFFF",
  },
  monthGridItemTextActive: {
    color: "#FFFFFF",
  },
  monthGridItemTextActiveDark: {
    color: "#FFFFFF",
  },
  pickerActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 12,
  },
  pickerButton: {
    minWidth: 88,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
    alignItems: "center",
  },
  pickerCancelButton: {
    backgroundColor: "#E5E7EB",
  },
  pickerApplyButton: {
    backgroundColor: "#007AFF",
  },
  pickerCancelText: {
    color: "#111111",
    fontWeight: "700",
  },
  pickerApplyText: {
    color: "#FFFFFF",
    fontWeight: "700",
  },
  // Search container styles
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    marginHorizontal: 16,
    marginVertical: 12,
    paddingHorizontal: 12,
    height: 44,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  searchContainerDark: {
    backgroundColor: "#2C2C2E",
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: "#000000",
  },
  searchInputDark: {
    color: "#FFFFFF",
  },
  // Request item styles
  requestItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  requestItemDark: {
    borderBottomColor: "#333333",
  },
  requestInfo: {
    flex: 1,
  },
  employeeName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#000000",
  },
  employeeNameDark: {
    color: "#FFFFFF",
  },
  requestDetail: {
    fontSize: 16,
    color: "#000000",
  },
  requestDetailDark: {
    color: "#D1D1D6",
  },
  // No requests text
  noRequestsText: {
    fontSize: 16,
    textAlign: "center",
    marginTop: 20,
    color: "#000000",
  },
  noRequestsTextDark: {
    color: "#FFFFFF",
  },
  content: {
    flex: 1,
  },
  requestList: {
    flexGrow: 1,
  },
  pdfButton: {
    padding: 8,
    marginRight: 8,
  },
  statsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    gap: 12,
    paddingHorizontal: 16,
    marginTop: 16,
  },
  statsCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  statsCardDark: {
    backgroundColor: "#333",
    shadowColor: "#FFF",
  },
  statsValue: {
    fontSize: 24,
    fontWeight: "600",
    color: "#000000",
    marginVertical: 8,
  },
  statsValueDark: {
    color: "#FFFFFF",
  },
  statsLabel: {
    fontSize: 14,
    color: "#666666",
    textAlign: "center",
  },
  statsLabelDark: {
    color: "#8E8E93",
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
    backgroundColor: "#FFFFFF",
    marginTop: 8,
    marginHorizontal: 16,
    borderRadius: 12,
  },
  paginationContainerDark: {
    backgroundColor: "#1E1E1E",
    borderTopColor: "#333333",
  },
  paginationButton: {
    padding: 8,
    borderRadius: 8,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 16,
    marginHorizontal: 16,
    color: "#000000",
  },
  paginationTextDark: {
    color: "#FFFFFF",
  },
  actionButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    gap: 8,
  },
  actionButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
});

const createDynamicStyles = (colorScheme) => ({
  container: {
    backgroundColor: colorScheme === "dark" ? "#121212" : "#f9f9f9",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colorScheme === "dark" ? "#1a2e22" : "#e6f0e8",
  },
});

export default LeaveReport;
